import { CostCalculationExternal } from 'generated';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';

import HoursDisplay from 'components/shared/cell-formatters/HoursDisplay';
import MonetaryDisplay from 'components/shared/cell-formatters/MonetaryDisplay';
import PercentageDisplay from 'components/shared/cell-formatters/PercentageDisplay';
import {
  GridContainer,
  GridCompact1,
  GridCompact2,
  GridCompact3,
  GridCompact4
} from 'components/shared/Grid/Grid.components';

type CostCalculationExternalMonthProperties = {
  record: CostCalculationExternal;
};

export default function CostCalculationExternalMonth(
  properties: CostCalculationExternalMonthProperties
) {
  const monthRecord = properties.record;
  const { t } = useTranslation('common');

  return (
    <>
      <Grid
        container
        display="flex"
        alignItems="start"
        mt={2}
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#EEEEEE'
        }}
      >
        <GridCompact2>{t('description')}</GridCompact2>
        <GridCompact1 style={{ textAlign: 'right' }}>{t('hours')}</GridCompact1>
        <GridCompact1 style={{ textAlign: 'right' }}>{t('rate')}</GridCompact1>
        <GridCompact1 style={{ textAlign: 'right' }}>
          {t('percentage')}
        </GridCompact1>
        <GridCompact1 style={{ textAlign: 'right' }}>{t('total')}</GridCompact1>
      </Grid>
      {monthRecord.hours.map((deployedHours, index) => (
        <Grid
          container
          key={index}
          display="flex"
          alignItems="start"
          columnSpacing={2}
        >
          <GridCompact2>{deployedHours.description}</GridCompact2>
          <GridCompact1>
            <HoursDisplay value={deployedHours.hours} />
          </GridCompact1>
          <GridCompact1>
            <MonetaryDisplay value={deployedHours.baseRate} />
          </GridCompact1>
          <GridCompact1>
            <PercentageDisplay value={deployedHours.percentage} />
          </GridCompact1>
          <GridCompact1>
            <MonetaryDisplay value={deployedHours.totalHours} />
          </GridCompact1>
        </Grid>
      ))}
      <Grid
        container
        display="flex"
        alignItems="start"
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'"
        }}
      >
        <GridCompact2>{t('hoursTotal')}</GridCompact2>
        <GridCompact3 />
        <GridCompact1 borderTop={1} textAlign="right">
          &#x21B3;
        </GridCompact1>
        <GridCompact1>
          <MonetaryDisplay value={monthRecord.totalHours} />
        </GridCompact1>
      </Grid>

      <Grid
        container
        display="flex"
        alignItems="start"
        mt={1}
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#EEEEEE'
        }}
      >
        <GridCompact2>{t('fee')}</GridCompact2>
        <GridCompact1 textAlign="right">{t('ofTotal')}</GridCompact1>
        <GridCompact2 />
        <GridCompact1 textAlign="right">{t('total')}</GridCompact1>
      </Grid>
      {monthRecord.fees.map((fee, index) => (
        <Grid
          container
          key={index}
          display="flex"
          alignItems="start"
          columnSpacing={2}
        >
          <GridCompact2>
            <PercentageDisplay value={fee.feePercentage} align="left" />
          </GridCompact2>
          <GridCompact1>
            <MonetaryDisplay value={fee.feeOfTotal} />
          </GridCompact1>
          <GridCompact1 />
          <GridCompact1>
            <MonetaryDisplay value={fee.total} />
          </GridCompact1>
        </Grid>
      ))}
      <Grid
        container
        display="flex"
        alignItems="start"
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'"
        }}
      >
        <GridCompact2>{t('feeTotal')}</GridCompact2>
        <GridCompact3 />
        <GridCompact1 borderTop={1} textAlign="right">
          &#x21B3;
        </GridCompact1>
        <GridCompact1>
          <MonetaryDisplay value={monthRecord.totalFees} />
        </GridCompact1>
      </Grid>

      <Grid
        container
        display="flex"
        alignItems="start"
        mt={1}
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#EEEEEE'
        }}
      >
        <GridCompact2>{t('travelExpenses')}</GridCompact2>
        <GridCompact1 textAlign="right">{t('km')}</GridCompact1>
        <GridCompact2 />
        <GridCompact1 textAlign="right">{t('total')}</GridCompact1>
      </Grid>
      <GridContainer display="flex" alignItems="start">
        <GridCompact2 />
        <GridCompact1>
          <HoursDisplay value={monthRecord.totalKm} />
        </GridCompact1>
        <GridCompact2 />
        <GridCompact1>
          <MonetaryDisplay value={monthRecord.totalTravelExpenses} />
        </GridCompact1>
      </GridContainer>
      <Grid
        container
        display="flex"
        alignItems="start"
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'"
        }}
      >
        <GridCompact2>{t('travelExpensesTotal')}</GridCompact2>
        <GridCompact3 />
        <GridCompact1 borderTop={1} textAlign="right">
          &#x21B3;
        </GridCompact1>
        <GridCompact1>
          <MonetaryDisplay value={monthRecord.totalTravelExpenses} />
        </GridCompact1>
      </Grid>

      <Grid
        container
        display="flex"
        alignItems="start"
        mt={1}
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#EEEEEE'
        }}
      >
        <GridCompact2>{t('vat')}</GridCompact2>
        <GridCompact1 textAlign="right">{t('ofTotal')}</GridCompact1>
        <GridCompact2 />
        <GridCompact1 textAlign="right">{t('total')}</GridCompact1>
      </Grid>
      {monthRecord.vat.map((v, index) => (
        <Grid
          container
          key={index}
          display="flex"
          alignItems="start"
          columnSpacing={2}
        >
          <GridCompact2>
            <PercentageDisplay value={v.vatPercentage} align="left" />
          </GridCompact2>
          <GridCompact1>
            <MonetaryDisplay value={v.vatOfTotal} />
          </GridCompact1>
          <GridCompact2 />
          <GridCompact1>
            <MonetaryDisplay value={v.total} />
          </GridCompact1>
        </Grid>
      ))}
      <Grid
        container
        display="flex"
        alignItems="start"
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'"
        }}
      >
        <GridCompact2>{t('vatTotal')}</GridCompact2>
        <GridCompact3 />
        <GridCompact1 borderTop={1} textAlign="right">
          &#x21B3;
        </GridCompact1>
        <GridCompact1 borderBottom={1}>
          <MonetaryDisplay value={monthRecord.totalVat} />
        </GridCompact1>
      </Grid>

      <Grid
        container
        display="flex"
        alignItems="start"
        mb={1}
        mt={1}
        columnSpacing={2}
        style={{
          fontFamily: "'SourceSansPro-Regular', Arial, Helvetica, 'sans-serif'",
          backgroundColor: '#CCCCCC'
        }}
      >
        <GridCompact2>{t('total')}</GridCompact2>
        <GridCompact4 />
        <GridCompact1 borderBottom={2}>
          <MonetaryDisplay value={monthRecord.total} />
        </GridCompact1>
      </Grid>
    </>
  );
}
